import React from 'react';
import { createIconElement } from '@/lib/utils/createIconElement';
import { StanButton } from '@/components';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface OnboardingStepProps {
    stepNumber: number;
    title: string;
    description: string;
    subtitle?: string;
    buttonText: string;
    isActive: boolean;
    isCompleted: boolean;
    onClick: () => void;
    icons?: Array<{ Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; className: string }>;
    placeholderIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    profileImageUrl?: string;
    userName?: string;
    skippable?: boolean;
    onSkip?: () => void;
    image?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export function OnboardingStep({
    stepNumber,
    title,
    description,
    subtitle,
    buttonText,
    isActive,
    isCompleted,
    onClick,
    icons,
    placeholderIcon: PlaceholderIcon,
    profileImageUrl,
    userName,
    skippable = false,
    onSkip,
    image: ImageComponent,
}: OnboardingStepProps) {
    const isLastStep = stepNumber === 4;
    const mobile = useMediaQuery('(max-width: 768px)');
    return (
        <div className={`flex gap-4 md:flex-row ${isActive ? 'flex-col pb-4 md:flex-row' : ''}`}>
            {/* Step number circle and connector line */}
            <div className='flex flex-row items-start'>
                <div className='relative flex w-10 flex-col items-center'>
                    <div
                        className={`relative z-10 flex size-10 items-center justify-center rounded-full border-2 text-base font-semibold ${
                            isActive
                                ? 'border-brand-main bg-canvas text-brand-main'
                                : isCompleted
                                  ? 'bg-brand-subtle text-primary-main border-neutral-subtle'
                                  : 'bg-neutral-light text-gray border-neutral-subtle'
                        }`}
                    >
                        {isCompleted ? '✓' : stepNumber}
                    </div>
                    {!isLastStep && (
                        <div
                            className={`bg-neutral-soft relative z-0 mt-2 h-8 w-0.5 md:block ${isActive ? 'md:h-45' : ''}`}
                        />
                    )}
                </div>
                {isActive && mobile && (
                    <div className='ml-2'>
                        <h2>Store Launch Checklist</h2>
                        <p className='text-gray text-sm'>{stepNumber - 1}/4 tasks completed</p>
                    </div>
                )}
            </div>

            {/* Step content */}
            <div
                className={`relative z-20 max-w-3xl flex-1 transition-all ${
                    isActive ? 'bg-neutral-light border-neutral-subtle rounded-md border p-5 shadow-sm' : ''
                }`}
            >
                {isActive ? (
                    /* Card content - 2 column layout when active */
                    <div className='flex flex-col items-center gap-4 md:flex-row'>
                        {/* Column 1: Image/icons - appears first on mobile, second on desktop */}
                        <div className='order-1 flex flex-shrink-0 items-center md:order-2'>
                            {/* Step image */}
                            {ImageComponent && (
                                <div className='flex size-40 items-center justify-center overflow-hidden rounded-lg'>
                                    <ImageComponent className='h-full w-full' />
                                </div>
                            )}

                            {/* Profile picture or placeholder icon (for step 1) */}
                            {!ImageComponent && profileImageUrl ? (
                                <div className='bg-neutral-light flex size-24 items-center justify-center overflow-hidden rounded-lg'>
                                    <img src={profileImageUrl} alt='Profile' className='h-full w-full object-cover' />
                                </div>
                            ) : !ImageComponent && PlaceholderIcon ? (
                                <div className='bg-neutral-light flex size-24 items-center justify-center rounded-lg'>
                                    {userName ? (
                                        <div className='text-brand-main text-2xl font-bold'>
                                            {userName.charAt(0).toUpperCase()}
                                        </div>
                                    ) : (
                                        createIconElement(PlaceholderIcon, { className: 'size-16 text-gray' })
                                    )}
                                </div>
                            ) : null}

                            {/* Social icons for step 2 - wrapped in a larger circle */}
                            {icons && icons.length > 0 && (
                                <div className='bg-neutral-light flex size-20 flex-shrink-0 items-center justify-center gap-1.5 rounded-full'>
                                    {icons.map(({ Icon, className }, index) => (
                                        <div
                                            key={index}
                                            className={`flex size-8 items-center justify-center rounded-full ${className}`}
                                        >
                                            {createIconElement(Icon, { className: 'size-4 text-canvas' })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Column 2: Title, subtitle, button - appears second on mobile, first on desktop */}
                        <div className='order-2 flex flex-1 flex-col gap-4 md:order-1'>
                            <div>
                                <h3 className='text-primary-main mb-1 text-lg font-bold'>{title}</h3>
                                <p className='text-gray text-sm leading-relaxed'>{description}</p>
                                {subtitle && <p className='text-primary-main mt-1 text-sm font-semibold'>{subtitle}</p>}
                            </div>
                            <StanButton
                                name={`onboarding-step-${stepNumber}-cta`}
                                onClick={onClick}
                                color='primary'
                                className='w-50'
                            >
                                {buttonText}
                            </StanButton>
                            {skippable && onSkip && (
                                <div
                                    onClick={onSkip}
                                    className='text-brand-main flex w-50 cursor-pointer items-center justify-center text-sm leading-relaxed'
                                >
                                    Skip for now
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Title and description - only visible when not active */
                    <div>
                        <h3 className='text-primary-main mb-1 text-lg font-bold'>{title}</h3>
                        <p className='text-gray text-sm leading-relaxed'>{description}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
